import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  PatchType,
  PostEditorialAssetDuplicateFullParams,
  PostEditorialAssetDuplicateResult,
} from 'utility/types/dataProvider';

type Options = {
  invalidateQueries?: boolean | string[];
  onSuccess?: (newUuid: string) => void;
  onError?: (error: ApiErrorResponse) => void;
  openInNewTab?: boolean;
};

type CreateFromTemplateArgs = {
  templateId: string;
  quickLaunchPrefill?: AssetQuickLaunchPrefill;
};

type DuplicateMutationVariables = PostEditorialAssetDuplicateFullParams & {
  quickLaunchPrefill?: AssetQuickLaunchPrefill;
};

/**
 * Builds JSON patch operations from quick launch prefill values.
 * These patches are applied to the duplicated asset to override template values.
 */
const buildQuickLaunchPatch = (prefill?: AssetQuickLaunchPrefill): PatchType[] => {
  if (!prefill) {
    return [];
  }

  const patches: PatchType[] = [];

  // Override workflow if provided
  if (prefill.workflowId !== undefined) {
    const workflowValueRaw = prefill.workflowId;
    const normalizedWorkflow =
      typeof workflowValueRaw === 'string' ? workflowValueRaw.trim() : workflowValueRaw;

    if (typeof normalizedWorkflow === 'string' && normalizedWorkflow.length > 0) {
      patches.push({
        path: '/properties/workflow_id',
        op: 'add',
        value: normalizedWorkflow as unknown as string,
      });
    } else if (normalizedWorkflow === null) {
      // Explicitly clear workflow if null is passed
      patches.push({
        path: '/properties/workflow_id',
        op: 'add',
        value: null as unknown as string,
      });
    }
  }

  // Override start time if provided
  if (prefill.startTime !== undefined) {
    const startValueRaw = prefill.startTime ?? null;
    const startValue =
      typeof startValueRaw === 'string' && startValueRaw.trim() === ''
        ? null
        : startValueRaw;
    patches.push({
      path: '/start_time',
      op: 'add',
      value: startValue as unknown as string,
    });
  }

  // Override slug if provided
  if (prefill.slug !== undefined) {
    const slugValue =
      typeof prefill.slug === 'string' ? prefill.slug.trim() : prefill.slug;
    if (typeof slugValue === 'string' && slugValue.length > 0) {
      patches.push({
        path: '/properties/print_slug',
        op: 'add',
        value: slugValue,
      });
    } else if (slugValue === null || slugValue === '') {
      // Clear slug if empty or null
      patches.push({
        path: '/properties/print_slug',
        op: 'add',
        value: null as unknown as string,
      });
    }
  }

  return patches;
};

/**
 * Applies quick launch prefill data to a newly created asset by patching its properties.
 * This is called after asset duplication to set up initial values for workflow, times, etc.
 */
const applyQuickLaunchPrefillPatch = async (
  dataProvider: BloxCustomDataProviderType,
  assetId: string,
  quickLaunchPrefill?: AssetQuickLaunchPrefill,
) => {
  const patchOps = buildQuickLaunchPatch(quickLaunchPrefill);
  if (!patchOps.length) {
    return;
  }

  await dataProvider.patch('editorial', {
    id: assetId,
    data: patchOps,
  });
};

/**
 * Hook to create a new asset from a story template.
 * This duplicates the template asset and applies:
 * 1. Sets template=false to make it a regular asset
 * 2. Sets published=0 and draft=true for new assets
 * 3. Applies any quick launch prefill values (workflow, start time, slug)
 *    which override the template's inherited values
 */
export function useCreateFromTemplate(options: Options = {}) {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const { queryClient } = useCosmosQueryClient();
  const { activeSite } = useCosmos();
  const { t } = useTranslation();

  const { mutateAsync, status } = useMutation<
    PostEditorialAssetDuplicateResult,
    ApiErrorResponse,
    DuplicateMutationVariables
  >({
    mutationKey: ['createFromTemplate'],
    mutationFn: ({ quickLaunchPrefill: _prefill, ...rest }) =>
      dataProvider.duplicateAssetById(rest),
    onSuccess: async (resp, variables) => {
      const newUuid = resp.data?.uuid;
      if (!newUuid) {
        console.warn('[useCreateFromTemplate] Duplicate succeeded, but no uuid returned');
        return;
      }

      const quickLaunchPrefill = variables?.quickLaunchPrefill;
      try {
        await applyQuickLaunchPrefillPatch(dataProvider, newUuid, quickLaunchPrefill);
      } catch (error) {
        console.error(
          '[useCreateFromTemplate] Failed to apply quick launch values:',
          error,
        );
        notify(t('blox.editorial.assetQuickLaunch.apply.warning_duplicate'), {
          type: 'warning',
        });
      }

      // Default invalidation - refresh React Admin's list
      if (options.invalidateQueries) {
        const keyArray =
          options.invalidateQueries === true
            ? ['editorial', 'getInfiniteList']
            : options.invalidateQueries;

        queryClient.invalidateQueries({ queryKey: keyArray });
      }

      // Open the editor page for the new asset
      const editorUrl = `/${activeSite}/editorial/edit/${newUuid}`;
      if (options.openInNewTab ?? true) {
        window.open(editorUrl, '_blank');
      } else {
        window.location.href = editorUrl;
      }
      options.onSuccess?.(newUuid);
    },
    onError: (err) => {
      notify(`${t('blox.general.error.label')}: ${err.message || err.axiosMessage}`, {
        type: 'error',
      });
      options.onError?.(err);
    },
  });

  const createFromTemplate = useCallback(
    async ({ templateId, quickLaunchPrefill }: CreateFromTemplateArgs) => {
      // Base patches:
      // 1. Set template=false to make it a regular asset
      // 2. Set published=0 for new assets
      // 3. Set draft=true for new assets
      const basePatch: PatchType[] = [
        { path: '/template', op: 'add', value: false },
        { path: '/published', op: 'add', value: 0 },
        { path: '/draft', op: 'add', value: true },
      ];

      const payload: DuplicateMutationVariables = {
        path: { id: templateId },
        body: {
          patch: basePatch,
        },
        quickLaunchPrefill,
      };

      return mutateAsync(payload);
    },
    [mutateAsync],
  );

  return { createFromTemplate, status };
}
